import type { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { Order } from '../data/databaseSchema';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { formatDateDdMmYyyy } from '../utils/dateUtils';
import { OrderDrawer } from './OrderDrawer';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

interface OrderListProps {
  orders: Order[];
  onEdit?: (order: Order) => void;
  onDelete?: (orderId: string) => void;
  onAdd?: () => void;
  isLoading?: boolean;
}

export const OrderList: React.FC<OrderListProps> = ({
  orders,
  onEdit,
  onDelete,
  onAdd,
  isLoading = false,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const justSavedRef = useRef(false);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  const handleAddClick = useCallback(() => {
    setEditingOrder(null);
    setPanelMode('add');
  }, []);

  const handleDrawerSave = useCallback(
    (order: Order) => {
      if (onEdit && panelMode === 'edit') {
        onEdit(order);
      } else if (onAdd && panelMode === 'add') {
        onAdd();
      }
    },
    [onEdit, onAdd, panelMode]
  );

  const handleFormClose = useCallback(() => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingOrder(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingOrder(null);
  }, []);

  const handleViewClose = useCallback(() => {
    setPanelMode('empty');
    setSelectedOrder(null);
    setEditingOrder(null);
  }, []);

  const handleRowClicked = useCallback((event: RowClickedEvent<Order>) => {
    if (!event.data) return;
    setSelectedOrder(event.data);
    setPanelMode('selected');
    setEditingOrder(null);
  }, []);

  const handleDelete = useCallback(
    (order: Order) => {
      if (onDelete) {
        onDelete(order.id);
        setSelectedOrder(null);
        setPanelMode('empty');
      }
    },
    [onDelete]
  );

  // Column definitions for the order grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Number',
        field: 'order_number',
        width: 110,
        minWidth: 100,
        maxWidth: 120,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
      {
        headerName: 'Date',
        field: 'order_date',
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => formatDateDdMmYyyy(params.value),
      },
      {
        headerName: 'Project',
        field: 'project',
        width: 250,
        minWidth: 200,
        maxWidth: 300,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueGetter: (params: any) => {
          const project = params.data?.project;
          if (!project) return 'N/A';
          const code = project.project_number || '';
          const name = project.name || '';
          return code && name ? `${code} - ${name}` : code || name || 'N/A';
        },
        cellRenderer: (params: any) => {
          const project = params.data?.project;
          if (!project) return 'N/A';
          const code = project.project_number || '';
          const name = project.name || '';
          if (!code && !name) return 'N/A';
          return `${code}${code && name ? ' - ' : ''}${name}`;
        },
      },
      {
        headerName: 'Comments',
        field: 'comments',
        flex: 1,
        minWidth: 200,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        valueFormatter: (params: any) => {
          if (params.value === null || params.value === undefined) return 'N/A';
          return params.value;
        },
        cellRenderer: (params: any) => {
          const comments = params.value;
          if (!comments || comments === 'N/A') return 'N/A';

          // Truncate long comments
          const maxLength = 100;
          if (comments.length > maxLength) {
            return <span title={comments}>{comments.substring(0, maxLength)}...</span>;
          }

          return comments;
        },
      },
    ],
    []
  );

  // Search functionality
  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      if (gridApi) {
        gridApi.setGridOption('quickFilterText', searchValue);
      }
    },
    [gridApi]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', '');
    }
  }, [gridApi]);

  // Export functionality
  const handleExportCSV = useCallback(() => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: 'orders.csv',
        columnKeys: ['id', 'projectId', 'name', 'date', 'comments'],
      });
    }
  }, [gridApi]);

  // Grid event handlers
  const handleGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const handleFirstDataRendered = useCallback(() => {
    // Data rendered
  }, []);

  const handleRowDataUpdated = useCallback(() => {
    // Row data updated
  }, []);

  const handleSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selectedNodes = gridApi.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedOrders(selectedData);
    }
  }, [gridApi]);

  // Grid options
  const gridOptions = {
    ...agGridDefaultOptions,
    theme: agGridTheme,
    suppressContextMenu: true,
    onGridReady: handleGridReady,
    onFirstDataRendered: handleFirstDataRendered,
    onRowDataUpdated: handleRowDataUpdated,
    onSelectionChanged: handleSelectionChanged,
    onRowClicked: handleRowClicked,
    rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    quickFilterText: searchTerm,
    defaultColDef: {
      ...agGridDefaultColDef,
      sortable: true,
    },
  };

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'edit' || panelMode === 'add' ? (
          <OrderDrawer
            isOpen={true}
            onClose={handleFormClose}
            onSave={handleDrawerSave}
            onEntrySaved={() => {
              justSavedRef.current = true;
              setPanelMode('empty');
              setSelectedOrder(null);
              onAdd?.();
            }}
            order={editingOrder}
            isEdit={panelMode === 'edit'}
          />
        ) : panelMode === 'selected' && selectedOrder ? (
          <OrderDrawer
            key={selectedOrder.id}
            isOpen={true}
            readOnly
            order={selectedOrder}
            onClose={handleViewClose}
            onSave={() => {}}
            onEditRequest={() => {
              setEditingOrder(selectedOrder);
              setPanelMode('edit');
            }}
            onDeleteRequest={onDelete ? () => handleDelete(selectedOrder) : undefined}
          />
        ) : null
      }
      list={
        <div className="flex min-h-0 flex-1 flex-col p-2">
          <div className="mb-6 flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-3 ">
              <div className="relative bg-white">
                <Input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-[250px] pr-8"
                />
                {searchTerm && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    onClick={handleClearSearch}
                    title="Clear search"
                  >
                    ×
                  </Button>
                )}
              </div>

              {selectedOrders.length > 0 && (
                <div className="flex items-center gap-2 rounded-md bg-primary-500/10 px-4 py-2 text-sm text-primary-600">
                  {selectedOrders.length} selected
                </div>
              )}

              <Button type="button" variant="secondary" onClick={handleExportCSV}>
                Export CSV
              </Button>
            </div>
            <Button type="button" onClick={handleAddClick}>
              <PlusIcon data-icon="inline-start" />
              Add Order
            </Button>
          </div>

          {/* AG Grid */}
          {(orders.length > 0 || isLoading) && (
            <>
              <style>{agGridThemeStyles}</style>
              <div
                className="ag-theme-material min-h-0 flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={orders}
                  columnDefs={columnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={gridOptions}
                  animateRows={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  rowNumbers={true}
                />

                {isLoading && (
                  <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg bg-surface-card px-8 py-4 text-sm font-medium text-text-secondary shadow-md">
                    <Spinner className="size-4" />
                    Loading orders...
                  </div>
                )}
              </div>
            </>
          )}

          {/* Empty state */}
          {orders.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
              <div className="text-5xl text-text-muted">📋</div>
              <div className="text-lg font-medium text-text-secondary">No orders found</div>
              <div className="text-sm text-text-muted">Add your first order to get started</div>
              <Button type="button" className="mt-2" onClick={handleAddClick}>
                <PlusIcon data-icon="inline-start" />
                Add Order
              </Button>
            </div>
          )}
        </div>
      }
    />
  );
};
